import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import logoImg from '../assets/image/logo.png';
import {
  LocationSVG,
  MobileIconSVG,
  WhatsappIconSVG,
  TimerSVG,
  UserIconSVG,
  MealIconSVG,
  TransferIconSVG,
  SightseeingSVG,
  HotelIconSVG,
  StarSVG,
} from '../components/SvgIcons';

const fallbackTours = Array.from({ length: 6 }, (_, index) => ({
  id: `fallback-${index + 1}`,
  image_url: logoImg,
  name: 'Leh Ladakh Kargil Trip From Srinagar',
  description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
  duration: '3 Days 2 Night',
  persons: '2 Person',
  price: '$149',
  old_price: '$299',
  location: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
  transport: 'Transport',
  meal: 'Meal',
  sightseeing: 'Sightseeing',
  hotel: 'Hotel',
  review_text: '(5 Reviews)',
  tour_type_label: 'Select Tour Type',
  sort_order: index,
}));

function normalizeTourPrice(value) {
  if (value === null || value === undefined || value === '') {
    return '$149';
  }

  if (typeof value === 'number') {
    return `$${value}`;
  }

  const stringValue = String(value).trim();
  return stringValue.startsWith('$') ? stringValue : `$${stringValue}`;
}

function normalizeDescription(value) {
  if (!value) {
    return 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.';
  }

  return value.length > 90 ? `${value.slice(0, 87)}...` : value;
}

function normalizeLocation(tour) {
  return tour.location || tour.start_city || tour.end_city || 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.';
}

function normalizeTours(tours, imageBaseUrl = '', fallbackLogoUrl = '') {
  if (!Array.isArray(tours) || tours.length === 0) {
    return fallbackTours.map((tour) => ({
      ...tour,
      image_url: fallbackLogoUrl || logoImg,
      fallback_image_url: fallbackLogoUrl || logoImg,
    }));
  }

  const normalizedBase = String(imageBaseUrl || '').trim().replace(/\/+$/, '');
  const remoteFallback = normalizedBase ? `${normalizedBase}/no-image.jpg` : '';
  const resolvedLogoFallback = String(fallbackLogoUrl || '').trim() || logoImg;

  return tours.map((tour, index) => ({
    id: tour.id || `tour-${index}`,
    name: tour.name || 'Tour Package',
    image_url: tour.image_url || '',
    fallback_image_url: resolvedLogoFallback || remoteFallback || logoImg,
    description: normalizeDescription(tour.description),
    duration: tour.duration || 'Custom Duration',
    persons: tour.persons || '2 Person',
    price: normalizeTourPrice(tour.price),
    old_price: tour.old_price || '$299',
    location: normalizeLocation(tour),
    transport: tour.transport || 'Transport',
    meal: 'Meal',
    sightseeing: 'Sightseeing',
    hotel: 'Hotel',
    review_text: '(5 Reviews)',
    tour_type_label: 'Select Tour Type',
    tour_types: Array.from(new Map(
      (Array.isArray(tour.tour_types) ? tour.tour_types : [])
        .map((type) => {
          const name = String(type?.name || '').trim();
          const alias = String(type?.alias || '').trim();
          const id = type?.id ?? alias ?? name;

          return name ? [`${id}-${name.toLowerCase()}`, { id, name, alias }] : null;
        })
        .filter(Boolean),
    ).values()),
  }));
}

function TourImage({ src = '', backupSrc = '', alt = '' }) {
  const candidates = [src, backupSrc, logoImg].filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleError = () => {
    setActiveIndex((current) => {
      if (current >= candidates.length - 1) {
        return current;
      }

      return current + 1;
    });
  };

  return (
    <img
      src={candidates[activeIndex]}
      alt={alt}
      className="trip-img"
      loading="lazy"
      onError={handleError}
    />
  );
}

const TripCard = ({
  tours = [],
  contact = {},
  heading = '',
  description = '',
  onSelectTour = null,
  contactEnabled = true,
  imageBaseUrl = '',
  fallbackLogoUrl = '',
}) => {
  const items = normalizeTours(tours, imageBaseUrl, fallbackLogoUrl);
  const mobileNumber = contact.mobile_primary || contact.mobile_secondary || '';
  const whatsappNumber = contact.whatsapp || contact.mobile_primary || '';

  return (
    <>
      {(heading || description) ? (
        <section className="heading-section mt-100 mb-30 plr">
          <Container className="text-center heading-container">
            {heading ? <h2 className="fw-bolder">{heading}</h2> : null}
            {description ? <p className="fw-semibold darkgray-text mb-0 title-paragraph">{description}</p> : null}
          </Container>
        </section>
      ) : null}

      <Container className="plr">
        <Row>
          {items.map((tour) => {
            const availableTourTypes = Array.isArray(tour.tour_types) && tour.tour_types.length
              ? tour.tour_types
              : [{ id: 'default-standard', name: 'Standard' }, { id: 'default-deluxe', name: 'Deluxe' }];

            return (
            <Col md={6} xl={4} key={tour.id}>
              <div className="trip-card">
                <div className="inner-card">
                  <TourImage
                    src={tour.image_url}
                    backupSrc={tour.fallback_image_url}
                    alt={tour.name}
                  />

                  <div className="trip-content">
                    <h3 className="fw-bold dark-black">{tour.name}</h3>
                    <p className="trip-desc d-flex font-14 darkgray-text">
                      <span><LocationSVG /></span>
                      {tour.location}
                    </p>

                    <div className="d-flex justify-content-between trip-information">
                      <div className="trip-info days-bg fw-semibold">
                        <span className="d-flex align-items-center gap-1">
                          <TimerSVG />
                          {tour.duration}
                        </span>
                        <span className="d-flex align-items-center gap-1">
                          <UserIconSVG />
                          {tour.persons}
                        </span>
                      </div>

                      {contactEnabled ? (
                        <div className="mobile-whtsapp">
                          <a
                            className="trip-info fw-semibold"
                            href={mobileNumber ? `tel:${mobileNumber}` : '#inquire-now'}
                            aria-label="Call now"
                          >
                            <span className="mobileicon"><MobileIconSVG /></span>
                          </a>
                          <a
                            className="trip-info"
                            href={whatsappNumber ? `https://wa.me/${String(whatsappNumber).replace(/\D/g, '')}` : '#inquire-now'}
                            target={whatsappNumber ? '_blank' : undefined}
                            rel={whatsappNumber ? 'noreferrer' : undefined}
                            aria-label="Chat on WhatsApp"
                          >
                            <span className="whtsapicon"><WhatsappIconSVG /></span>
                          </a>
                        </div>
                      ) : null}
                    </div>

                    <div className="trip-icons dark-black">
                      <p className="mb-0"><span><MealIconSVG /></span> {tour.meal}</p>
                      <p className="mb-0"><span><TransferIconSVG /></span> {tour.transport}</p>
                      <p className="mb-0"><span><SightseeingSVG /></span> {tour.sightseeing}</p>
                      <p className="mb-0"><span><HotelIconSVG /></span> {tour.hotel}</p>
                    </div>

                    <div className="trip-action">
                      <select className="form-select" defaultValue="">
                        <option value="" disabled>{tour.tour_type_label}</option>
                        {availableTourTypes.map((type) => (
                          <option key={`${tour.id}-${type.id}-${type.name}`} value={type.alias || type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      <a
                        href="#inquire-now"
                        className="offer-btn fw-semibold"
                        onClick={(event) => {
                          if (typeof onSelectTour === 'function') {
                            event.preventDefault();
                            onSelectTour(tour.name);
                          }
                        }}
                      >
                        Get Offer Price
                      </a>
                    </div>
                  </div>
                </div>

                <div className="d-flex price-card shadow-sm w-100">
                  <div className="price-left d-flex flex-column justify-content-center text-white">
                    <div className="text-decoration-line-through opacity-75 fw-semibold delete-price">{tour.old_price}</div>
                    <div className="fw-bold current-price">
                      {tour.price} <span className="fw-medium per-person text-white">/ Per person</span>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-1 bg-white">
                    <span><StarSVG /></span>
                    <span className="fw-medium review darkgray-text">{tour.review_text}</span>
                  </div>
                </div>
              </div>
            </Col>
            );
          })}
        </Row>
      </Container>
    </>
  );
};

export default TripCard;
